/**
 * Lifecycle band — the stage tracker shown in the AccountDetail header.
 *
 * A vertical accordion of the configured stages (from the active Accounts-view
 * config, so both the stage count and labels are tenant/portal-configurable):
 * completed stages collapse to a single line, the current stage auto-expands to
 * reveal its sub-stage checklist, and upcoming stages stay collapsed. Each
 * stage / sub-stage carries a done / current / upcoming state. The "Lifecycle"
 * header is itself a master collapse: clicking it folds the entire band down to
 * a one-line summary of the current stage · sub-stage (on top of the per-stage
 * accordions).
 *
 * When the caller may edit (any non-empty `editableSubStages`), an "Edit
 * lifecycle" button is shown — it opens the Move-group modal
 * (MoveLifecycleModal) directly via `onEditLifecycle`. Every manual move goes
 * through that one modal (destination picker + RFC gate + transition history);
 * the old inline override select and the click-a-checklist-step affordance
 * were removed (mirrors the synrgyinfinity 2026-07-24 lifecycle-editor
 * rework). The actual transition happens on the canonical sub-stage — the
 * server re-derives the view keys from the config (see
 * resolveViewKeysForScopes).
 */

import React from 'react';
import {
  Check,
  CheckCircle2,
  ChevronDown,
  Circle,
  CircleDot,
  Lock,
  Pencil,
  SkipForward,
  X,
} from 'lucide-react';
import type {LifecyclePhase, AccountsSubStage} from '@api/generated/client';
import {cn} from '@common-web/lib/utils';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Popover, PopoverContent, PopoverTrigger} from '@common-web/components/ui/popover';
import {useAccountsViewConfig} from '@common-web/hooks/useAccountsViewConfig';
import {
  CANONICAL_SUB_STAGE_PHASE,
  DEFAULT_ACCOUNTS_VIEW_CONFIG,
  findPositionBySystemSubStage,
  representedPhases,
  resolveAccountPosition,
} from '@common/accountsView';
import {SUB_STAGE_LABEL, toActiveSubStage} from '@common-web/components/account/phaseLabels';

interface LifecycleBandProps {
  account: {
    lifecyclePhase: LifecyclePhase;
    subStage: AccountsSubStage;
    viewStageKey?: string | null;
    viewSubStageKey?: string | null;
    /** When the account was created — dates the first sub-stage when no
     *  transition has been recorded for it yet. */
    createdAt?: Date | string | null;
    /** Per-sub-stage entered/left timestamps (canonical sub-stage → ISO),
     *  reconstructed server-side. Powers the inline dates + durations.
     *  `skipped` marks stages a forward move passed over with an explicit
     *  skip — always carrying the mover's reason and identity. */
    stageTimeline?: {
      enteredAt: Record<string, string>;
      leftAt: Record<string, string>;
      skipped?: Record<string, {reason: string; at: string; by: string | null}>;
    } | null;
    /** Analyst date edits (sub-stage → ISO, or '' for a cleared date). Marks
     *  edited stamps and keeps cleared steps empty (no auto-date fallback). */
    stageDateOverrides?: Partial<Record<string, string>> | null;
  };
  /**
   * Sub-stages the caller may manually move to. When non-empty, the "Edit
   * lifecycle" control is shown — it opens the Move-group modal (owned by the
   * parent via `onEditLifecycle`).
   */
  editableSubStages?: AccountsSubStage[];
  /** Open the Move-group modal (MoveLifecycleModal) — the one manual-move surface. */
  onEditLifecycle?: () => void;
  isTransitioning?: boolean;
  /**
   * Contextual "do the next thing" control, rendered inline on the right of the
   * current sub-stage row (mirrors the synrgyinfinity lifecycle band).
   */
  nextAction?: React.ReactNode;
  /**
   * When true, an inline "Skip" control is rendered on the current sub-stage row
   * (mirrors the synrgyinfinity band) so admins can move the account forward one
   * step. The parent owns the mutation via `onSkip`. The band only surfaces the
   * control while the account is still in the Lead (Prospect) phase — the RFC
   * step (Onboarding) is never skippable (admins must generate the RFC), and the
   * Implementation/CBS stages stay internal-admin-driven — plus one Onboarding
   * exception: the post-RFC-approval Proposal step (Synrgy) is skippable for
   * deals that don't need a proposal.
   */
  canSkip?: boolean;
  /** Advance the account to `target` (the immediate next sub-stage), recording
   *  the required skip reason against the stage being skipped. */
  onSkip?: (target: AccountsSubStage, reason: string) => void;
  /**
   * Whether this account's tenant has the "Welcome Call" implementation step
   * (Synrgy/Quantum default-on, server-resolved per account). When false, any
   * WELCOME_CALL step in the active config (the internal ladder carries one for
   * its cross-tenant board) is hidden from this account's tracker.
   */
  welcomeCallEnabled?: boolean;
  /**
   * Edit a step's entered-at date (internal analysts). When provided, every
   * dated step's stamp becomes editable via an inline date input — the demo's
   * auto-stamp-then-edit flow. `null` clears the step's date entirely (for
   * accidentally recorded dates). The parent owns the mutation.
   */
  onEditStageDate?: (subStage: AccountsSubStage, isoDate: string | null) => void;
  /**
   * Start with the master collapse folded to the one-line summary (used once
   * an account is Active, so the finished tracker doesn't overwhelm the page).
   * Initial state only — the user's toggle wins afterwards.
   */
  defaultCollapsed?: boolean;
}

// Skipping is confined to the Lead (Prospect) phase, with one exception below
// (the Proposal step). The RFC step (Onboarding) must be entered deliberately by
// generating the RFC — never by skipping — so Onboarding is otherwise excluded.
// The Implementation/CBS stages are driven by their own flow (advanceCbsStage)
// and are likewise never skippable here.
const SKIPPABLE_PHASES: ReadonlySet<LifecyclePhase> = new Set<LifecyclePhase>(['LEAD']);

/**
 * Radix occasionally leaks `pointer-events: none` on <body> when portaled
 * modals close in quick succession (the Move-stage dialog handing off to
 * the parent's confirm dialog), leaving the page unclickable until a refresh.
 * Clear the lock once the close animation has finished — same mitigation the
 * shared Layout applies on route changes.
 */
export function clearRadixPointerEventsLock(): void {
  window.setTimeout(() => {
    // Another modal may have (re)opened during the delay (e.g. the confirm
    // dialog this modal hands off to). Its Radix lock is then live and owns
    // the body style — clearing it would let clicks through the overlay — so
    // only clear once no open dialog remains.
    const openDialog = document.querySelector(
      '[data-state="open"][role="dialog"], [data-state="open"][role="alertdialog"]',
    );
    if (openDialog) return;
    document.body.style.pointerEvents = '';
  }, 500);
}

function toDate(value: Date | string): Date {
  return value instanceof Date ? value : new Date(value);
}

/** Compact date for the per-step stamps, e.g. "Jun 12". */
function fmtShortDate(value: Date | string): string {
  return toDate(value).toLocaleDateString('en-US', {month: 'short', day: 'numeric'});
}

function fmtDateTime(value: Date | string): string {
  return toDate(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Whole days between two timestamps (never negative). */
function daysBetween(from: Date | string, to: Date | string): number {
  return Math.max(0, Math.floor((toDate(to).getTime() - toDate(from).getTime()) / 86_400_000));
}

/** Whole days from `from` to now (never negative) — time spent in the current stage. */
function daysSince(from: Date | string): number {
  return Math.max(0, Math.floor((Date.now() - toDate(from).getTime()) / 86_400_000));
}

// Who owns the forward move out of each sub-stage — surfaced as a colored pill
// on the current step (mirrors the synrgyinfinity band's owner tint). Purely
// informational: the actual action lives in the inline `nextAction` control.
type ActionOwner = 'Broker' | 'Admin' | 'Client' | 'CBS';

const OWNER_TINT: Record<ActionOwner, string> = {
  Broker: 'bg-blue-500/10 text-blue-700 dark:text-blue-300',
  Admin: 'bg-violet-500/10 text-violet-700 dark:text-violet-300',
  Client: 'bg-slate-500/10 text-slate-600 dark:text-slate-300',
  CBS: 'bg-teal-500/10 text-teal-700 dark:text-teal-300',
};

const SUB_STAGE_OWNER: Partial<Record<AccountsSubStage, ActionOwner>> = {
  NEW_LEAD: 'Broker',
  SWOT_CALL: 'Admin',
  PROPOSAL_SENT: 'Broker',
  PROPOSAL_PRESENTATION: 'Broker',
  DRAFT: 'Broker',
  SUBMITTED: 'Admin',
  BROKER_REVIEW: 'Broker',
  BROKER_APPROVED: 'Broker',
  CLIENT_REVIEW: 'Client',
  CLIENT_APPROVED: 'Client',
  PROPOSAL: 'Broker',
  MSA_PENDING: 'Admin',
  MSA_SENT: 'Client',
  MSA_SIGNED: 'Admin',
  SUBMITTED_FOR_IMPLEMENTATION: 'CBS',
  RFC_RECEIVED: 'CBS',
  WELCOME_CALL: 'CBS',
  CENSUS_BUILD_OUT: 'CBS',
  CENSUS_REVIEW: 'CBS',
  CENSUS_UPDATE_NEEDED: 'CBS',
  CENSUS_APPROVED: 'CBS',
  IMPLEMENTATION_CALL: 'CBS',
  DRY_RUN_1: 'CBS',
  FINANCIAL_CALL: 'CBS',
  DRY_RUN_2: 'CBS',
  GO_LIVE_RUN: 'CBS',
};

/** ISO/date → `yyyy-MM-dd` for the native date input (local time). */
function toDateInputValue(value: Date | string): string {
  const d = toDate(value);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * A step's date stamp with an inline edit flow: clicking the stamp opens a
 * popover with a date input (the demo auto-stamps each passed step and lets
 * the analyst adjust it). Saves at local noon so the picked calendar day never
 * shifts across timezones. Every dated step offers "Clear", which empties the
 * date entirely (for accidentally recorded dates); a cleared step renders a
 * "Set date" trigger so a date can be recorded again.
 */
function EditableStepDate({
  enteredAt,
  title,
  display,
  isCorrected,
  onSave,
}: {
  /** Absent when the step's date was cleared — the trigger reads "Set date". */
  enteredAt?: Date | string;
  title: string;
  display: string;
  isCorrected: boolean;
  onSave: (isoDate: string | null) => void;
}): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState(() => (enteredAt ? toDateInputValue(enteredAt) : ''));

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setValue(enteredAt ? toDateInputValue(enteredAt) : '');
      }}
    >
      <PopoverTrigger asChild>
        <button
          type='button'
          onClick={(e) => e.stopPropagation()}
          title={`${title}${isCorrected ? ' · Edited by an analyst' : ''} · Click to edit this date`}
          className='group/date inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded px-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground'
        >
          {display}
          {isCorrected ? <span aria-hidden>*</span> : null}
          <Pencil className='h-2.5 w-2.5 opacity-0 transition-opacity group-hover/date:opacity-100' />
        </button>
      </PopoverTrigger>
      <PopoverContent align='end' className='w-auto p-3' onClick={(e) => e.stopPropagation()}>
        <div className='flex items-center gap-2'>
          <Input
            type='date'
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className='h-8 w-40 text-xs'
          />
          <Button
            type='button'
            size='sm'
            className='h-8'
            disabled={!value}
            onClick={() => {
              onSave(new Date(`${value}T12:00:00`).toISOString());
              setOpen(false);
            }}
          >
            Save
          </Button>
          {enteredAt ? (
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-8 px-2 text-muted-foreground hover:text-foreground'
              title='Clear this date'
              onClick={() => {
                onSave(null);
                setOpen(false);
              }}
            >
              <X className='mr-1 h-3.5 w-3.5' />
              Clear
            </Button>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * The band's inline "Skip" control. Skipping always requires a reason (the
 * server rejects a skip without one), so the button opens a small popover
 * collecting it before firing the mutation.
 */
function SkipWithReasonControl({
  disabled,
  onSkip,
}: {
  disabled: boolean;
  onSkip: (reason: string) => void;
}): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const [reason, setReason] = React.useState('');

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setReason('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant='ghost'
          size='sm'
          className='h-auto px-2 py-1 text-xs text-muted-foreground hover:text-foreground'
          disabled={disabled}
          onClick={(e) => e.stopPropagation()}
        >
          Skip
          <SkipForward className='ml-1 h-3 w-3' />
        </Button>
      </PopoverTrigger>
      <PopoverContent align='end' className='w-72 p-3' onClick={(e) => e.stopPropagation()}>
        <div className='space-y-2'>
          <p className='text-xs font-medium text-foreground'>Why is this step being skipped?</p>
          <Input
            type='text'
            value={reason}
            autoFocus
            placeholder='Reason (required)'
            onChange={(e) => setReason(e.target.value)}
            className='h-8 text-xs'
          />
          <div className='flex justify-end gap-2'>
            <Button type='button' variant='ghost' size='sm' onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              type='button'
              size='sm'
              disabled={reason.trim().length === 0 || disabled}
              onClick={() => {
                onSkip(reason.trim());
                setOpen(false);
              }}
            >
              Skip step
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function LifecycleBand({
  account,
  editableSubStages = [],
  onEditLifecycle,
  isTransitioning = false,
  nextAction,
  canSkip = false,
  onSkip,
  welcomeCallEnabled = true,
  onEditStageDate,
  defaultCollapsed = false,
}: LifecycleBandProps): React.ReactElement {
  const canEdit = editableSubStages.length > 0 && !!onEditLifecycle;
  const stageTimeline = account.stageTimeline;
  const createdAt = account.createdAt ?? undefined;

  const activeConfig = useAccountsViewConfig();
  // The detail page (unlike the list) isn't phase-floored, so an internal user
  // can open an account whose phase the active (e.g. implementation-forward)
  // config doesn't represent. Rendering the stage tracker against a config that
  // doesn't contain the account would clamp it to the first stage and falsely
  // mark earlier steps complete — so fall back to the full default pipeline for
  // those out-of-view accounts.
  const config = (representedPhases(activeConfig) as string[]).includes(account.lifecyclePhase)
    ? activeConfig
    : DEFAULT_ACCOUNTS_VIEW_CONFIG;
  // The Welcome Call step is per-tenant (Synrgy/Quantum only). The internal
  // ladder carries it for every account (the board is cross-tenant), so drop it
  // from this account's tracker when its tenant has the flag off.
  const stages = React.useMemo(() => {
    if (welcomeCallEnabled) return config.stages;
    return config.stages
      .map((stage) => ({
        ...stage,
        subStages: stage.subStages.filter((s) => s.systemSubStage !== 'WELCOME_CALL'),
      }))
      .filter((stage) => stage.subStages.length > 0);
  }, [config, welcomeCallEnabled]);

  // The band highlights the milestone the account is working toward (see
  // toActiveSubStage): a submitted RFC sits on Broker Approval, a broker-approved
  // RFC sits on Client Approval, etc., with every earlier step shown completed.
  // The milestone is positioned through the active Accounts-view config; prefer
  // the account's own stored position when it already sits on the milestone, and
  // fall back to the account's position when the config doesn't anchor it.
  const displaySubStage = toActiveSubStage(account.subStage);
  const position =
    (displaySubStage === account.subStage
      ? resolveAccountPosition(config, account)
      : findPositionBySystemSubStage(config, displaySubStage)) ??
    resolveAccountPosition(config, account);

  const foundStageIdx = position ? stages.findIndex((s) => s.key === position.stage.key) : -1;
  // Clamp defensively so a degenerate config never renders out of bounds.
  const currentIdx = foundStageIdx >= 0 ? foundStageIdx : 0;
  const currentStage = stages[currentIdx];
  const foundSubIdx =
    position && currentStage
      ? currentStage.subStages.findIndex((s) => s.key === position.subStage.key)
      : -1;
  const currentSubIdx = foundSubIdx >= 0 ? foundSubIdx : 0;

  // Time spent in the current sub-stage — powers the "Nd in stage" chip on the
  // current stage header. Dates from when the account entered the current
  // sub-stage (or its creation, for the very first step, when no transition has
  // been recorded yet).
  const currentSubSys = currentStage?.subStages[currentSubIdx]?.systemSubStage;
  // An analyst-cleared date ('' override) suppresses the created-at fallback —
  // a cleared step must stay dateless.
  const currentCleared =
    currentSubSys != null && account.stageDateOverrides?.[currentSubSys] === '';
  const currentEnteredAt =
    (currentSubSys ? stageTimeline?.enteredAt[currentSubSys] : undefined) ??
    (currentIdx === 0 && currentSubIdx === 0 && !currentCleared ? createdAt : undefined);
  const daysInStage = currentEnteredAt != null ? daysSince(currentEnteredAt) : null;

  // The immediate next sub-stage (config order) the current account can be
  // skipped forward to: the next sub-stage in the current stage, otherwise the
  // first sub-stage of the next stage. Offered when that target still lands
  // within the Lead (Prospect) phase — the RFC step (Onboarding) is never
  // skippable, and the CBS stages are driven by their own flow — or when the
  // account sits on the post-RFC-approval Proposal step (Synrgy), which is
  // skippable to the next configured Onboarding sub-stage (MSA Package Sent for
  // Synrgy, the same place saving a proposal would advance to).
  const skipTarget: AccountsSubStage | null = React.useMemo(() => {
    if (!canSkip || !onSkip || isTransitioning) return null;
    const nextSub =
      currentStage?.subStages[currentSubIdx + 1] ?? stages[currentIdx + 1]?.subStages[0] ?? null;
    if (!nextSub) return null;
    const target = nextSub.systemSubStage as AccountsSubStage;
    const leadSkip =
      SKIPPABLE_PHASES.has(account.lifecyclePhase) &&
      SKIPPABLE_PHASES.has(CANONICAL_SUB_STAGE_PHASE[target]);
    const proposalSkip =
      account.subStage === 'PROPOSAL' && CANONICAL_SUB_STAGE_PHASE[target] === 'ONBOARDING';
    if (!leadSkip && !proposalSkip) return null;
    return target;
  }, [
    canSkip,
    onSkip,
    isTransitioning,
    account.lifecyclePhase,
    account.subStage,
    currentStage,
    currentSubIdx,
    stages,
    currentIdx,
  ]);

  const skipControl =
    skipTarget && onSkip ? (
      <SkipWithReasonControl
        disabled={isTransitioning}
        onSkip={(reason) => onSkip(skipTarget, reason)}
      />
    ) : null;

  // null = "default": only the current stage is expanded.
  const [expandedStages, setExpandedStages] = React.useState<Set<string> | null>(null);
  // Completed stages collapse to one line; reveal them only on request.
  const [showCompleted, setShowCompleted] = React.useState(false);
  // Master collapse: folds the whole band to a one-line summary of the
  // current position (on top of the per-stage accordions).
  const [bandCollapsed, setBandCollapsed] = React.useState(defaultCollapsed);

  const isExpanded = (stageKey: string): boolean =>
    (expandedStages ?? new Set<string>(currentStage ? [currentStage.key] : [])).has(stageKey);

  const toggleStage = (stageKey: string): void => {
    const base = expandedStages ?? new Set<string>(currentStage ? [currentStage.key] : []);
    const next = new Set(base);
    if (next.has(stageKey)) {
      next.delete(stageKey);
    } else {
      next.add(stageKey);
    }
    setExpandedStages(next);
  };

  const visibleStages = stages.filter((_, i) => showCompleted || i >= currentIdx);

  // One-line "where the account is" summary shown while the band is collapsed.
  const currentSubLabel =
    currentStage?.subStages[currentSubIdx]?.label ?? SUB_STAGE_LABEL[displaySubStage];
  const collapsedSummary = currentStage
    ? `${currentStage.label} · ${currentSubLabel}`
    : SUB_STAGE_LABEL[displaySubStage];

  return (
    <div>
      <div className='mb-2 flex items-center justify-between gap-2'>
        <button
          type='button'
          onClick={() => setBandCollapsed((v) => !v)}
          aria-expanded={!bandCollapsed}
          title={bandCollapsed ? 'Expand lifecycle' : 'Collapse lifecycle'}
          className='group/band flex min-w-0 items-center gap-1.5 rounded px-1 py-0.5 text-left transition-colors hover:bg-muted'
        >
          <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
            Lifecycle
          </p>
          {bandCollapsed ? (
            <span className='min-w-0 truncate text-xs normal-case tracking-normal text-muted-foreground'>
              · {collapsedSummary}
            </span>
          ) : null}
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
              bandCollapsed && '-rotate-90',
            )}
          />
        </button>
        {canEdit && !bandCollapsed ? (
          // Soft-orange pill matching the header's in-pipeline stage pill tones.
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='h-auto rounded-full bg-[#fdeedd] px-3 py-1 text-xs font-semibold text-[#d96f10] hover:bg-[#fbdfc2] hover:text-[#b85c0a]'
            onClick={onEditLifecycle}
          >
            <Pencil className='mr-1 h-3 w-3' />
            Move stage
          </Button>
        ) : null}
      </div>

      {bandCollapsed ? null : (
        <>
          {canEdit ? (
            <p className='mb-2 text-xs text-muted-foreground'>
              Use “Move stage” to move this account to another stage. Past Onboarding, steps are
              driven by CBS.
            </p>
          ) : null}

          {currentIdx > 0 ? (
            <button
              type='button'
              onClick={() => setShowCompleted((v) => !v)}
              className='mb-1 flex w-full items-center gap-2 rounded-lg px-1 py-2 text-left transition-colors hover:bg-muted'
            >
              <CheckCircle2 className='h-4 w-4 shrink-0 text-primary' />
              <span className='truncate text-xs text-muted-foreground'>
                {stages
                  .slice(0, currentIdx)
                  .map((s) => s.label)
                  .join(' · ')}{' '}
                complete
              </span>
              <ChevronDown
                className={cn(
                  'ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
                  showCompleted && 'rotate-180',
                )}
              />
            </button>
          ) : null}

          <div className='-mx-1'>
            {visibleStages.map((stage) => {
              const stageIdx = stages.findIndex((s) => s.key === stage.key);
              const stageDone = stageIdx < currentIdx;
              const stageCurrent = stageIdx === currentIdx;
              const stageUpcoming = stageIdx > currentIdx;
              const expanded = isExpanded(stage.key);
              const steps = stage.subStages;
              const stageStepIdx = stageCurrent ? currentSubIdx : stageDone ? steps.length : -1;
              // A completed stage was left when the account entered the next stage's
              // first sub-stage — surfaced as the stage's completion date.
              const nextStageFirstSys = stages[stageIdx + 1]?.subStages[0]?.systemSubStage;
              const stageDoneDate =
                stageDone && nextStageFirstSys
                  ? stageTimeline?.enteredAt[nextStageFirstSys]
                  : undefined;
              // A stage counts as an Implementation stage when it holds CBS-driven
              // sub-stages — it gets the "Updated automatically by CBS" footer.
              const isImplementationStage = steps.some(
                (s) =>
                  CANONICAL_SUB_STAGE_PHASE[s.systemSubStage as AccountsSubStage] ===
                  'IMPLEMENTATION',
              );
              const summary = stageCurrent
                ? `${steps[stageStepIdx]?.label ?? SUB_STAGE_LABEL[displaySubStage]}${
                    steps.length > 1 ? ` · ${stageStepIdx + 1} of ${steps.length}` : ''
                  }`
                : stageDone
                  ? `Completed${stageDoneDate ? ` · ${fmtShortDate(stageDoneDate)}` : ''}`
                  : 'Upcoming';

              return (
                <div key={stage.key} className='border-b border-border last:border-0'>
                  <button
                    type='button'
                    onClick={() => toggleStage(stage.key)}
                    className='flex w-full items-center gap-2.5 rounded-lg px-1 py-2.5 text-left transition-colors hover:bg-muted'
                  >
                    {stageDone ? (
                      <CheckCircle2 className='h-[18px] w-[18px] shrink-0 text-primary' />
                    ) : stageCurrent ? (
                      <CircleDot className='h-[18px] w-[18px] shrink-0 text-primary' />
                    ) : (
                      <Circle className='h-[18px] w-[18px] shrink-0 text-muted-foreground/40' />
                    )}
                    <span
                      className={cn(
                        'text-sm font-semibold',
                        stageUpcoming ? 'text-muted-foreground' : 'text-foreground',
                      )}
                    >
                      {stage.label}
                    </span>
                    <span className='min-w-0 truncate text-xs text-muted-foreground'>
                      · {summary}
                    </span>
                    <span className='ml-auto flex shrink-0 items-center gap-2'>
                      {stageCurrent && daysInStage != null ? (
                        <span className='inline-flex items-center whitespace-nowrap rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground'>
                          {daysInStage}d in stage
                        </span>
                      ) : null}
                      <ChevronDown
                        className={cn(
                          'h-[15px] w-[15px] shrink-0 text-muted-foreground transition-transform',
                          expanded && 'rotate-180',
                        )}
                      />
                    </span>
                  </button>

                  {expanded ? (
                    <ul className='pb-2 pl-7 pr-1'>
                      {steps.map((sub, i) => {
                        const sDone = stageDone || (stageCurrent && i < stageStepIdx);
                        const sCurrent = stageCurrent && i === stageStepIdx;
                        // Date next to each entered sub-stage (when it was entered);
                        // completed steps also show the time they spent in-stage.
                        const sys = sub.systemSubStage;
                        // A passed step that was explicitly skipped renders a
                        // "Skipped" marker (reason + who on hover) instead of a
                        // completion check — skipped steps are never shown as
                        // silently completed.
                        const skipInfo =
                          sDone && !sCurrent ? stageTimeline?.skipped?.[sys] : undefined;
                        // '' override = analyst cleared the date: no stamp at
                        // all, including the created-at fallback for the very
                        // first step.
                        const isCleared = account.stageDateOverrides?.[sys] === '';
                        const enteredAt =
                          stageTimeline?.enteredAt[sys] ??
                          (stageIdx === 0 && i === 0 && createdAt && !isCleared
                            ? createdAt
                            : undefined);
                        const leftAt = stageTimeline?.leftAt[sys];
                        const stageDays =
                          sDone && !sCurrent && enteredAt && leftAt
                            ? daysBetween(enteredAt, leftAt)
                            : null;
                        const stampTitle = enteredAt
                          ? `Entered ${fmtDateTime(enteredAt)}${
                              stageDays != null
                                ? ` · ${stageDays} day${stageDays === 1 ? '' : 's'} in stage`
                                : ''
                            }`
                          : '';
                        const stampDisplay = enteredAt
                          ? `${fmtShortDate(enteredAt)}${stageDays != null ? ` · ${stageDays}d` : ''}`
                          : '';
                        const skipTitle = skipInfo
                          ? `Skipped by ${skipInfo.by ?? 'Unknown'} on ${fmtDateTime(skipInfo.at)} — ${skipInfo.reason}`
                          : '';
                        // Editors also get a "Set date" trigger on a cleared
                        // step so an accidental clear stays reversible. A
                        // skipped step shows its "Skipped" badge; editors can
                        // still record a date for it, which un-skips it
                        // server-side.
                        const dateStamp = skipInfo ? (
                          <span className='flex shrink-0 items-center gap-1.5'>
                            <span
                              title={skipTitle}
                              className='inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400'
                            >
                              <SkipForward className='h-2.5 w-2.5' />
                              Skipped
                            </span>
                            {onEditStageDate ? (
                              <EditableStepDate
                                title='Record the date this step actually happened (un-skips it)'
                                display='Set date'
                                isCorrected={false}
                                onSave={(iso) => onEditStageDate(sys as AccountsSubStage, iso)}
                              />
                            ) : null}
                          </span>
                        ) : onEditStageDate ? (
                          enteredAt || isCleared ? (
                            <EditableStepDate
                              enteredAt={enteredAt}
                              title={enteredAt ? stampTitle : 'No date recorded'}
                              display={enteredAt ? stampDisplay : 'Set date'}
                              isCorrected={!!account.stageDateOverrides?.[sys]}
                              onSave={(iso) => onEditStageDate(sys as AccountsSubStage, iso)}
                            />
                          ) : null
                        ) : enteredAt ? (
                          <span
                            className='shrink-0 whitespace-nowrap text-xs text-muted-foreground'
                            title={stampTitle}
                          >
                            {stampDisplay}
                          </span>
                        ) : null;
                        const owner = sCurrent
                          ? SUB_STAGE_OWNER[sub.systemSubStage as AccountsSubStage]
                          : undefined;
                        const ownerPill = owner ? (
                          <span
                            className={cn(
                              'inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold',
                              OWNER_TINT[owner],
                            )}
                          >
                            {owner}
                          </span>
                        ) : null;
                        const trailing =
                          sCurrent && (ownerPill || nextAction || skipControl) ? (
                            <span
                              className='flex shrink-0 items-center gap-1.5'
                              onClick={(e) => e.stopPropagation()}
                            >
                              {ownerPill}
                              {nextAction}
                              {skipControl}
                            </span>
                          ) : null;
                        // Checklist steps are display-only — every manual move goes
                        // through the Move-group modal ("Move stage"). The old
                        // click-a-step-to-set-it affordance was removed.
                        return (
                          <li key={sub.key}>
                            <div
                              className={cn(
                                '-mx-2 flex items-center gap-2.5 rounded-lg px-2 py-1.5',
                                sCurrent && 'border border-border bg-background',
                              )}
                            >
                              {skipInfo ? (
                                <SkipForward
                                  className='h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400'
                                  aria-label='Skipped'
                                />
                              ) : sDone ? (
                                <Check className='h-3.5 w-3.5 shrink-0 text-primary' />
                              ) : sCurrent ? (
                                <CircleDot className='h-3.5 w-3.5 shrink-0 text-primary' />
                              ) : (
                                <Circle className='h-3.5 w-3.5 shrink-0 text-muted-foreground/40' />
                              )}
                              <span
                                className={cn(
                                  'text-xs',
                                  sCurrent
                                    ? 'font-semibold text-foreground'
                                    : sDone
                                      ? 'text-muted-foreground'
                                      : 'text-muted-foreground/70',
                                )}
                              >
                                {sub.label}
                              </span>
                              {dateStamp || trailing ? (
                                <span className='ml-auto flex shrink-0 items-center gap-2 pl-2'>
                                  {dateStamp}
                                  {trailing}
                                </span>
                              ) : null}
                            </div>
                          </li>
                        );
                      })}
                      {isImplementationStage ? (
                        <li className='mt-1.5 flex items-center gap-1.5 pl-2 text-xs text-muted-foreground'>
                          <Lock className='h-3 w-3' /> Updated automatically by CBS
                        </li>
                      ) : null}
                    </ul>
                  ) : null}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
